'use client';

import * as DS from '@aurorasolar/ds';
import Image, { type ImageProps } from 'next/image';
import Link from 'next/link';
import { useMemo } from 'react';

import { useTracking } from '@/analytics';
import { TruncateText } from '@/app/home-solar/blog/BlogOverview/TruncateText';
import { EventData, EventWithPayload } from '@/helpers/tracking';

import { ButtonLink } from './ButtonLink';

const quoteUrl = process.env.NEXT_PUBLIC_BASE_URL || 'https://quote.aurorasolar.com';

type CardProps = {
  buttonProps: DS.IButton;
  columnCount?: 1 | 2 | 3 | 4;
  description?: string;
  event: {
    data?: EventData;
    name: EventWithPayload;
  };
  headline: string;
  image?: ImageProps;
  url: string;
};

export const PagePreviewCard = ({ description, event, headline, image, url, buttonProps, columnCount }: CardProps) => {
  const { trackEvent } = useTracking();

  const trackNavigate = () => {
    if (event.name) trackEvent(event.name, event.data ?? {});
  };

  const resolvedPath = useMemo(() => {
    if (url.startsWith('/home-solar')) {
      return url;
    }
    return `${quoteUrl}${url}`;
  }, [url]);

  const textProps = useMemo((): { description: DS.IText; headline: DS.IText } => {
    if (!columnCount || columnCount <= 2) {
      return {
        description: { text: 'body14' },
        headline: {
          as: 'h4',
          fontSize: { _: '20px', lg: 'h2' },
          fontWeight: 300,
          lineHeight: { _: '24px', md: '30px' },
        },
      };
    }

    if (columnCount === 3) {
      return {
        description: { text: { _: 'body9', md: 'body12', xl: 'body14' } },
        headline: {
          as: 'h4',
          fontSize: { _: 'h3', md: '20px', xl: 'h2' },
          fontWeight: 300,
          lineHeight: { _: '24px' },
        },
      };
    }

    return {
      description: { text: { _: 'body9', lg: 'body12', xl: 'body14' } },
      headline: {
        as: 'h4',
        fontSize: { _: 'h3', lg: '20px', xl: 'h2' },
        fontWeight: 300,
        lineHeight: { _: '24px' },
      },
    };
  }, [columnCount]);

  return (
    <DS.Card borderWidth={0} flexGrow={1} p={0}>
      <DS.Box maxH={288} overflow="hidden" w="100%">
        <Link href={resolvedPath} onClick={trackNavigate}>
          {image ? (
            <Image
              alt={image.alt}
              height={288}
              src={image.src}
              style={{ height: 'auto', objectFit: 'cover', width: '100%' }}
              width={376}
            />
          ) : (
            <DS.Box bg="uiBgAlternate" h={220} w="100%" />
          )}
        </Link>
      </DS.Box>

      <DS.Flex flexDirection="column" flexGrow={1} gap={2} px={{ _: 0, md: 2 }}>
        <Link href={resolvedPath} onClick={trackNavigate}>
          <DS.Text {...textProps.headline}>{headline}</DS.Text>
        </Link>

        {description ? <TruncateText {...textProps.description}>{description}</TruncateText> : null}
        <DS.Flex flexDirection="column" flexGrow={1} justifyContent="end">
          <ButtonLink event={event} path={resolvedPath} {...buttonProps} />
        </DS.Flex>
      </DS.Flex>
    </DS.Card>
  );
};
